export type Language = 'id' | 'en'

export const LINKS = {
  login: 'https://tokoteta.id/auth/login',
  register: 'https://tokoteta.id/auth/register',
  instagram: 'https://www.instagram.com/tokoteta.id/',
  tiktok: 'https://www.tiktok.com/@tokoteta.id',
  facebook: 'https://www.facebook.com/tokoteta.id',
}

/**
 * Inline markers used by utils/richText:
 *   **text** -> bold, dark
 *   __text__ -> brand blue, semibold
 */
export const copy = {
  en: {
    nav: {
      how: 'How it works',
      why: 'Why us?',
      login: 'Log in',
      getStarted: 'Get Started',
      menu: 'Menu',
    },
    hero: {
      quoteLabel: 'Joko Prabuwesi says:',
      quote:
        '"**Stop paying** for corporate marketing! Every time you shop, you\'re funding their billion-dollar ad campaigns. What if that __money went to you instead?__"',
      titleLead: 'Shop. Share. ',
      titleAccent: 'Earn.',
      body: "It's time to change the rules! At __TokoTeta__, you can finally buy well-known brands at **lower prices**, and even **earn money** by recommending your favorite products to others.",
      primaryCta: 'Start Shopping',
      secondaryCta: 'How it works',
      badgeTitle: 'Tokoteta -',
      badgeSubtitle: 'the new way of shopping',
      ambassadorLabel: 'TokoTeta Ambassador',
      ambassadorName: 'JOKO PRABUWESI',
    },
    broken: {
      title: 'Shopping Should Pay You Back, Not Just Cost You',
      body: 'Every single day brands spend huge amounts of money on ads. In the end, that cost is built right into the price of the products you buy.',
      highlightTitle: 'At __TokoTeta__, we do things differently.',
      highlightBody:
        'Instead of spending it all on ads, we share part of that budget back with the people who recommend the products.',
      points: [
        {
          emoji: '💸',
          title: 'Brands Spend Big on Ads',
          text: 'Every year, billions are spent just to find new customers.',
        },
        {
          emoji: '🛒',
          title: 'Shoppers End Up Paying',
          text: 'Those promotion costs are usually baked into the price you pay.',
        },
        {
          emoji: '🎉',
          title: 'Now You Get a Share',
          text: 'Shop as usual, recommend products you love, then collect cashback and commission.',
        },
      ],
    },
    brands: {
      titleLead: 'Your favorite brands,',
      titleRest: 'now with ',
      titleAccent: 'cashback.',
      body: 'Shop viral names like Whitelab, Scarlett, Skintific, Herborist and many others at lower prices. Plus, you can **earn extra income** simply by recommending these products to your followers on social media.',
      more: '+ Many More',
    },
    steps: {
      title: 'Three steps to smarter shopping',
      subtitle: "It's simple, transparent, and rewarding.",
      items: [
        {
          title: 'Shop',
          text: 'Browse trusted local and global brands at prices that skip the marketing markup.',
        },
        {
          title: 'Share',
          text: 'Send your personal product link to friends or post it to your social media.',
        },
        {
          title: 'Earn',
          text: 'Every purchase through your link pays you a commission, straight to your wallet.',
        },
      ],
    },
    cta: {
      title: "It's not just companies winning anymore.",
      quoteLabel: 'Joko invites you:',
      quote:
        '"Join the revolution of fair, community-powered shopping today."',
      button: 'Create Free Account',
      note: 'No credit card required. Start earning today.',
    },
    footer: {
      description:
        'Tokoteta connects trusted people with trusted products. Join the social commerce revolution.',
      links: ['About Us', 'Terms of Service', 'Privacy Policy', 'Contact'],
      rights: '© 2026 TokoTeta. All rights reserved.',
      supported: 'Initiative supported by',
      followUs: 'Follow TokoTeta on social media',
    },
  },
  id: {
    nav: {
      how: 'Cara kerja',
      why: 'Kenapa kami?',
      login: 'Masuk',
      getStarted: 'Mulai Sekarang',
      menu: 'Menu',
    },
    hero: {
      quoteLabel: 'Joko Prabuwesi berkata:',
      quote:
        '"**Berhenti membayar** biaya marketing perusahaan! Setiap kali kamu belanja, kamu ikut mendanai iklan mereka yang bernilai miliaran. Bagaimana kalau __uang itu kembali ke kamu?__"',
      titleLead: 'Belanja. Bagikan. ',
      titleAccent: 'Cuan.',
      body: 'Saatnya mengubah aturan! Di __TokoTeta__, kamu bisa membeli brand ternama dengan **harga lebih hemat**, bahkan **dapat penghasilan** hanya dengan merekomendasikan produk favoritmu.',
      primaryCta: 'Mulai Belanja',
      secondaryCta: 'Lihat cara kerja',
      badgeTitle: 'Tokoteta -',
      badgeSubtitle: 'cara baru berbelanja',
      ambassadorLabel: 'Ambassador TokoTeta',
      ambassadorName: 'JOKO PRABUWESI',
    },
    broken: {
      title: 'Belanja Harusnya Bikin Untung, Bukan Cuma Keluar Uang',
      body: 'Setiap hari brand menghabiskan banyak uang untuk iklan. Pada akhirnya, biaya itu ikut masuk ke harga produk yang kamu beli.',
      highlightTitle: 'Di __TokoTeta__, kami melakukan hal yang berbeda.',
      highlightBody:
        'Daripada menghabiskan semuanya untuk iklan, sebagian anggaran tersebut kami bagikan kembali kepada orang yang merekomendasikan produk.',
      points: [
        {
          emoji: '💸',
          title: 'Brand Keluar Banyak Uang untuk Iklan',
          text: 'Setiap tahun, miliaran rupiah dihabiskan untuk mencari pelanggan baru.',
        },
        {
          emoji: '🛒',
          title: 'Ujung-Ujungnya Pembeli yang Bayar',
          text: 'Biaya promosi biasanya sudah masuk ke harga produk yang kamu beli.',
        },
        {
          emoji: '🎉',
          title: 'Sekarang Kamu Ikut Kebagian',
          text: 'Belanja seperti biasa, rekomendasikan produk yang kamu suka, lalu dapatkan cashback dan komisi.',
        },
      ],
    },
    brands: {
      titleLead: 'Brand favoritmu,',
      titleRest: 'kini dengan ',
      titleAccent: 'cashback.',
      body: 'Belanja brand viral seperti Whitelab, Scarlett, Skintific, Herborist, dan banyak lainnya dengan harga lebih hemat. Kamu juga bisa **dapat penghasilan tambahan** hanya dengan merekomendasikan produk ini ke followers-mu.',
      more: '+ Masih Banyak Lagi',
    },
    steps: {
      title: 'Tiga langkah belanja lebih cerdas',
      subtitle: 'Sederhana, transparan, dan menguntungkan.',
      items: [
        {
          title: 'Belanja',
          text: 'Pilih brand lokal dan global terpercaya dengan harga tanpa beban biaya iklan.',
        },
        {
          title: 'Bagikan',
          text: 'Kirim link produk pribadimu ke teman atau bagikan ke media sosialmu.',
        },
        {
          title: 'Cuan',
          text: 'Setiap pembelian lewat link-mu memberi komisi langsung ke dompetmu.',
        },
      ],
    },
    cta: {
      title: 'Bukan cuma perusahaan yang untung lagi.',
      quoteLabel: 'Joko mengajak kamu:',
      quote:
        '"Gabung revolusi belanja yang adil dan digerakkan komunitas hari ini."',
      button: 'Buat Akun Gratis',
      note: 'Tanpa kartu kredit. Mulai dapat penghasilan hari ini.',
    },
    footer: {
      description:
        'Tokoteta menghubungkan orang terpercaya dengan produk terpercaya. Bergabunglah dengan revolusi social commerce.',
      links: ['Tentang Kami', 'Syarat Layanan', 'Kebijakan Privasi', 'Kontak'],
      rights: '© 2026 TokoTeta. Seluruh hak dilindungi.',
      supported: 'Inisiatif didukung oleh',
      followUs: 'Ikuti TokoTeta di media sosial',
    },
  },
}

export type Copy = (typeof copy)['en']
